<template>
  <section class="f-col gap-8" data-test-id="vf-footer-newsletter">
    <div class="f-col items-center gap-2 text-center">
      <h2
        class="w-full ws-pre-wrap title-2 c-brand-2 -rotate-3"
        data-test-id="vf-footer-newsletter-title"
      >
        {{ heading }}
      </h2>
      <p
        v-if="subheading"
        class="mt-4 body-4"
        data-test-id="vf-footer-newsletter-subtitle"
      >
        {{ subheading }}
      </p>
    </div>

    <base-form
      v-if="isEmailState || isSmsState"
      ref="formRef"
      class="f-col gap-8"
      data-test-id="form-footer-newsletter"
      :form
      name="footer-newsletter"
      @submit="submit"
    >
      <div class="f-col gap-4">
        <vf-form-field
          v-if="isEmailState"
          v-slot="{ attrs }"
          name="email"
          :rule="[validateRequired($t.newsletterEmailAddress), validateEmail()]"
          variant="inverse"
        >
          <div class="w-full space-y-2">
            <input-email
              v-model="form.email"
              v-bind="attrs"
              class="h-14 w-full b rounded-xs px-4 o:bg-brand-1"
              :class="(loading || !!storedPhone.number) && 'cursor-not-allowed'"
              :disabled="loading || !!email"
              :label="$t.newsletterEmailAddress"
              variant="inverse"
            />
            <div v-if="apiError" class="mt-4 text-sm c-red-60">
              {{ apiError }}
            </div>
          </div>
        </vf-form-field>

        <vf-form-field
          v-if="isEmailState"
          v-slot="{ attrs }"
          class-wrapper="space-y-2"
          name="policy"
          :rule="validateRequired('', $t.footerNewsletter.consentCheckbox)"
          variant="inverse"
        >
          <vf-checkbox
            v-model="form.policy"
            v-bind="attrs"
            :disabled="loading"
            required
            :size="checkboxSize"
            variant="inverse"
          >
            <span class="text-micro" data-test-id="vf-footer-newsletter-consent">
              <form-legal-disclaimer
                :content="replaceAll($t.footerNewsletter.policy, { brandName: $t.brandName })"
                :link-classes="{ privacy: 'underline' }"
              />
            </span>
          </vf-checkbox>
        </vf-form-field>

        <vf-form-field
          v-if="isSmsState"
          v-slot="{ attrs }"
          name="phone"
          :rule="[validateRequired($t.mobileNumber), validatePhone(form.phoneCode)]"
          variant="inverse"
        >
          <input-phone
            v-model="form.phone"
            v-model:code="form.phoneCode"
            class="h-14 w-full b rounded-xs px-4"
            :class="(loading || !!storedPhone.number) && 'cursor-not-allowed'"
            v-bind="attrs"
            :disable-code="!mobilePhone.enableCountryCodeSelection"
            :disabled="loading || !!storedPhone.number"
            required
            variant="inverse"
          />
          <div v-if="apiError" class="mt-4 text-sm c-red-60">
            {{ apiError }}
          </div>
        </vf-form-field>

        <vf-form-field
          v-if="isSmsState"
          v-slot="{ attrs }"
          class-wrapper="space-y-2"
          name="smsAccepted"
          :rule="validateRequired('', $t.footerNewsletter.consentCheckbox)"
          variant="inverse"
        >
          <vf-checkbox
            v-model="form.smsAccepted"
            v-bind="attrs"
            :disabled="loading"
            required
            :size="checkboxSize"
            variant="inverse"
          >
            <span class="text-micro">
              <form-legal-disclaimer
                :content="$t.smsTerms"
                :link-classes="{ 'sms-terms': 'underline', 'privacy': 'underline' }"
              />
            </span>
          </vf-checkbox>
        </vf-form-field>
      </div>

      <vf-button
        class="w-full"
        :disabled="loading"
        :loading
        :size="buttonSize"
        type="submit"
        variant="primary-inverse"
      >
        {{ $t.footerNewsletter.submit }}
      </vf-button>
    </base-form>

    <div
      v-else-if="isSmsPendingState"
      class="f-col gap-8"
      data-test-id="vf-footer-newsletter-pending"
    >
      <div class="f-col gap-4">
        <input-phone
          v-model="storedPhone.number"
          class="h-14 w-full b rounded-xs px-4"
          disabled
          :show-country-code="false"
          variant="inverse"
        />
      </div>
    </div>
    <dialog-sms-enrollment class-content="o:p-0 o:h-fit" />
    <dialog-sms-enrollment-confirmation class-content="o:p-0 o:h-fit" />
  </section>
</template>

<script lang="ts" setup>
import type { DialogBaseOptions } from '#types/config/components/dialog'
import type { FormLocation } from '#types/gtm'

const { formLocation = 'inline:single:footer:none' } = defineProps<{
  formLocation?: FormLocation
}>()

const emit = defineEmits<{ complete: [] }>()

const {
  vf: { footer: { newsletter: { checkboxSize, buttonSize } } },
  form: { signUp: { mobilePhone } }
} = useAppConfig().components

const { $gtm, $optimizely, $t, $feature } = useNuxtApp()
const { validateEmail, validatePhone, validateRequired } = useLocalizedValidators()
const { getUserEventData } = useAnalytics()
const route = useRoute()
const auth = useAuthStore()
const subscriptions = useSubscriptions()
const user = useUserStore()
const formRef = useTemplateRef('formRef')

const dialogOptions = {
  type: { sm: 'panel', lg: 'modal' },
  size: 'sm',
  position: 'bottom'
} as DialogBaseOptions

const DialogSmsEnrollment = createDialog('sms-enrollment', dialogOptions)
const DialogSmsEnrollmentConfirmation = createDialog('sms-enrollment-confirmation', dialogOptions)

const emailSubmittedFromTwoStepFlow = ref(false)
const loading = ref(false)
const campaignName = ref('FOOTER-EML-ONLY')

const form = reactive({
  email: '',
  policy: false,
  phone: '',
  phoneCode: getPhoneCodeByCountryCode(useCountryCode()) || '',
  smsAccepted: false
})

const apiError = ref('')

const email = computed(() => user.details?.email)
const storedPhone = computed(() => {
  const [code, number] = parseE164Phone(subscriptions.smsNumber.value)
  return { code, number }
})

const hasEmailSubscription = computed(() => subscriptions.newsletterSubscribed.value)
const hasSmsSubscription = computed(() => !!subscriptions.smsSubscription.value?.optin)
const isSmsConfirmed = computed(() => !!subscriptions.smsSubscription.value?.doubleOptin)
const isSubscriptionComplete = computed(() => hasEmailSubscription.value && isSmsConfirmed.value)

const currentStep = computed(() => {
  if (hasSmsSubscription.value && !isSmsConfirmed.value) return 'sms-pending-confirmation'
  if (emailSubmittedFromTwoStepFlow.value && !hasSmsSubscription.value) return 'success'
  if (!hasEmailSubscription.value) return 'email-step'
  if (!hasSmsSubscription.value) return 'sms-step-inline'
  return 'success'
})

const isEmailState = computed(() => currentStep.value === 'email-step')
const isSmsState = computed(() => currentStep.value === 'sms-step-inline')
const isSmsPendingState = computed(() => currentStep.value === 'sms-pending-confirmation')
const isSuccessState = computed(() => currentStep.value === 'success')

const heading = computed(() => {
  if (isSmsState.value) return $t.footerNewsletter.smsHeading
  if (isSmsPendingState.value) return $t.footerNewsletter.smsPendingHeading
  if (isSuccessState.value) return $t.footerNewsletter.emailSuccessHeading
  return $feature.enableAuthenticatedSubscriptionHeading
    ? auth.loggedIn
      ? $t.footerNewsletter.heading
      : $t.footerNewsletter.offerHeading
    : $t.footerNewsletter.heading
})

const subheading = computed(() => {
  if (isSmsState.value) return $t.footerNewsletter.smsSubheading
  if (isSmsPendingState.value) return $t.footerNewsletter.smsPendingMessage
  if (isSuccessState.value) return $t.footerNewsletter.emailSuccessMessage
  return ''
})

const dismissApiError = () => {
  apiError.value = ''
}

const resetPhoneForm = () => {
  form.phone = storedPhone.value.number || ''
  form.phoneCode = storedPhone.value.code || getPhoneCodeByCountryCode(useCountryCode()) || ''
  form.smsAccepted = false
}

const resetEmailForm = () => {
  form.email = email.value || ''
  form.policy = false
}

const submitEmail = async () => {
  const shouldPromptForSms = !hasEmailSubscription.value && !hasSmsSubscription.value

  const sms = shouldPromptForSms
    ? await DialogSmsEnrollment.open({ formLocation, phone: storedPhone.value.number })
    : undefined

  if (sms) {
    campaignName.value = 'cdpdialogueemlsms'
    if (auth.loggedIn) {
      await user.updateBasicInfo({
        phone: sms.phone,
        phoneCode: sms.phoneCode
      })
    }
  }

  $gtm.push('newsletterForm.onSubmit', formLocation, undefined, campaignName.value)

  const consumerId = await subscriptions.newsletter({
    optIn: true,
    campaignName: campaignName.value,
    recipient: email.value || form.email,
    ...(!email.value && { email: form.email, acquisitionType: 'Signup' }),
    ...(sms && { sms })
  })

  if (sms) DialogSmsEnrollmentConfirmation.open()

  const userEventData = await getUserEventData({ email: form.email }, consumerId)
  $gtm.push('user.onLoadUserData', userEventData)
  $optimizely?.dataReady(userEventData)
  $gtm.push('newsletterForm.onConfirmed', formLocation, campaignName.value)

  emailSubmittedFromTwoStepFlow.value = shouldPromptForSms

  resetEmailForm()
}

const submitSms = async () => {
  const formattedPhone = formatLocalNumberToE164(form.phone, form.phoneCode)

  if (auth.loggedIn) {
    await user.updateBasicInfo({
      phone: formattedPhone,
      phoneCode: form.phoneCode
    })
  }

  await subscriptions.sms(formattedPhone, form.phoneCode, true)

  resetPhoneForm()
}

const submit = async () => {
  if (loading.value) return

  try {
    loading.value = true
    dismissApiError()

    if (isEmailState.value) await submitEmail()
    else if (isSmsState.value) await submitSms()
  }
  catch (err) {
    if (isError(err)) {
      apiError.value = err.message
    }
  }
  finally {
    loading.value = false
  }
}

const reset = () => {
  resetEmailForm()
  resetPhoneForm()
  formRef.value?.reset()
  dismissApiError()
}

watch(() => route.path, dismissApiError)
watch(email, reset, { immediate: true })
whenever(isSubscriptionComplete, () => emit('complete'), { immediate: true })

onMounted(() => $gtm.push('newsletterForm.onImpression', formLocation, undefined, campaignName.value))
onActivated(reset)
</script>
